/* Mezkla site-wide header scroll transition.

   Toggles two body classes based on scrollY:
     nav-bar-hidden  — announcement bar slides up (scroll > 50, hysteresis at 30)
     nav-fixed       — header turns linho-solid + position:fixed (scroll > 80,
                       hysteresis at 64); on pages without [data-has-hero="true"]
                       it's added on load and never removed.

   Also dispatches a `mezkla:scroll` CustomEvent on document so other components
   (the floating filter pill on the collection page) can react without adding
   their own scroll listener. detail = { scrollY, lastScrollY, deltaY }.

   Single shared scroll listener for the whole theme. No framework, no deps.
*/
(function () {
  'use strict';

  var body = document.body;
  if (!body) return;

  var hasHero = body.dataset.hasHero === 'true';

  /* /pages/* templates (about, shipping, terms, contact, …): the header
     hides while scrolling down so it never rides over the page text, and
     slides back in the moment the user scrolls up. CSS lives in
     header.liquid under `body.template-page.nav-scroll-hidden`. */
  var hideOnScroll = body.classList.contains('template-page');

  var FIXED_ON  = 80;
  var FIXED_OFF = 64;
  var BAR_OFF   = 50;
  var BAR_ON    = 30;
  /* Hysteresis around the "intro section" bottom. What counts as the intro:
     - landing → `.hero-section` (full-bleed hero)
     - collection → `.collection-hero` (50vh image hero)
     - cart → `[data-cart-title-divider]` (hairline below the CART title)
     - any other template → tag its hero element with [data-hero-fade-marker]
       (about page hero band, /collections first cover) to opt in.
     When `body.past-hero` fires, CSS in header.liquid decides what to fade:
     landing fades nav + cart only (logo stays); collection, cart, about and
     list-collections fade the entire header. */
  var PAST_HERO_ON  = 0;  /* set on first scroll once boundary is measured */
  var PAST_HERO_OFF = 0;

  function recalcHeroBoundaries() {
    /* First matching marker wins. The generic [data-hero-fade-marker] hook lets
       any template opt into the fade without touching this file again. */
    var marker = document.querySelector(
      '.hero-section, .collection-hero, [data-cart-title-divider], [data-hero-fade-marker]'
    );
    if (!marker) return;
    /* Which edge of the marker is the boundary. Default = BOTTOM edge (header
       fades once the whole intro element has scrolled past — landing hero,
       collection hero, cart hairline, the CHAPTERS title). Tag the marker
       with data-hero-fade-edge="top" to use its TOP edge instead, so the
       header fades the moment the element reaches the header — the About
       page's first image. */
    var rect = marker.getBoundingClientRect();
    var useTop = marker.getAttribute &&
      marker.getAttribute('data-hero-fade-edge') === 'top';
    var edge = useTop ? rect.top : rect.bottom;
    /* Absolute Y coordinate of that edge in the document. */
    var scrollY = window.scrollY || window.pageYOffset || 0;
    var boundaryY = scrollY + edge;
    if (useTop) {
      /* Top-edge markers (about hero image) sit very high on the page, so the
         bottom-edge offsets below would push the release point to 0 and the
         header could never reappear. Tighter band: fade as the image tucks
         under the header, reappear only back at the very top. */
      PAST_HERO_ON = Math.max(0, boundaryY - 40);
      PAST_HERO_OFF = Math.max(0, boundaryY - 90);
    } else {
      /* "past" once we've scrolled within 80px of the boundary; release only
         after coming back well above it to prevent flicker. */
      PAST_HERO_ON = Math.max(0, boundaryY - 80);
      PAST_HERO_OFF = Math.max(0, boundaryY - 200);
    }
  }
  recalcHeroBoundaries();
  window.addEventListener('resize', recalcHeroBoundaries);

  var lastY = 0;
  var ticking = false;

  function update() {
    var y = window.scrollY || window.pageYOffset || 0;
    var deltaY = y - lastY;

    /* Header solid state */
    if (hasHero) {
      var navFixed = body.classList.contains('nav-fixed');
      if (!navFixed && y > FIXED_ON) body.classList.add('nav-fixed');
      else if (navFixed && y < FIXED_OFF) body.classList.remove('nav-fixed');
    }
    /* Non-hero pages: nav-fixed is set on load below; never removed. */

    /* Announcement bar visibility */
    var barHidden = body.classList.contains('nav-bar-hidden');
    if (!barHidden && y > BAR_OFF) body.classList.add('nav-bar-hidden');
    else if (barHidden && y < BAR_ON) body.classList.remove('nav-bar-hidden');

    /* Past-hero state — any page with a measured intro boundary (landing,
       collection, cart, about, list-collections). PAST_HERO_ON is only > 0
       once a marker was found, so it's the real gate. What fades is decided
       in CSS: landing fades nav + cart, the rest fade the entire header. */
    if (PAST_HERO_ON > 0) {
      var pastHero = body.classList.contains('past-hero');
      if (!pastHero && y > PAST_HERO_ON) body.classList.add('past-hero');
      else if (pastHero && y < PAST_HERO_OFF) body.classList.remove('past-hero');
    }

    /* Direction-based hide/show on /pages/* templates. The 2px dead zone
       filters out sub-pixel jitter and rubber-band bounce; near the top
       (y < 80) the header is always shown so the page never loads bare. */
    if (hideOnScroll) {
      var navHidden = body.classList.contains('nav-scroll-hidden');
      if (y < 80) {
        if (navHidden) body.classList.remove('nav-scroll-hidden');
      } else if (deltaY > 2 && !navHidden) {
        body.classList.add('nav-scroll-hidden');
      } else if (deltaY < -2 && navHidden) {
        body.classList.remove('nav-scroll-hidden');
      }
    }

    /* Broadcast for downstream components (filter pill, etc.) */
    document.dispatchEvent(new CustomEvent('mezkla:scroll', {
      detail: { scrollY: y, lastScrollY: lastY, deltaY: deltaY }
    }));

    lastY = y;
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(update);
      ticking = true;
    }
  }

  /* Non-hero pages start in scrolled state — sets the solid header up-front
     so users don't see a flash of transparent-over-light during load. */
  if (!hasHero) body.classList.add('nav-fixed');

  /* Initial sync (handles browser-restored scroll positions). */
  update();

  window.addEventListener('scroll', onScroll, { passive: true });
})();
